import React from 'react';
import { CopyableText } from 'src/shared/components';
import type { BatchJobStatus, DSMStatus, TerrainTileStatus } from 'src/shared/enums';
import { convertFromMeters, convertFromSquareMeters } from 'src/shared/helpers';
import { areaToDistanceUnit, polygonUnitAbbreviations } from '../constant';
import { PolygonMeasurementType, PolygonMeasurementUnit } from '../enums';
import { MeasurementInfo } from '../measurement-info';
import type { FeaturePropertiesType } from '../types';

type PolygonMeasurementDetailsProps = {
  featureProperties: FeaturePropertiesType;
  measurementUnit?: string;
  isLoading: boolean;
  terrainIterationId?: string | null;
  dsmStatus?: DSMStatus;
  dsmCogStatus?: BatchJobStatus;
  terrainTileStatus?: TerrainTileStatus;
};

export const PolygonMeasurementDetails: React.FC<
  PolygonMeasurementDetailsProps
> = ({
  featureProperties,
  measurementUnit,
  isLoading,
  terrainIterationId,
  dsmStatus,
  dsmCogStatus,
  terrainTileStatus,
}) => {
  // Variables.
  const { planarArea, surfaceArea, planarPerimeter, surfacePerimeter } =
    featureProperties;
  const areaUnit =
    measurementUnit ??
    polygonUnitAbbreviations[PolygonMeasurementUnit.SquareMeters];

  const perimeterUnit = areaToDistanceUnit[areaUnit];

  const polygonArea = planarArea
    ? convertFromSquareMeters(planarArea, areaUnit)
    : '-';
  const polygonPerimeter = planarPerimeter
    ? convertFromMeters(planarPerimeter, perimeterUnit)
    : '-';

  return (
    <div className="map-3d-feature-info-sidecard__measurement-details__polygon">
      {/* Area of Polygon */}
      <CopyableText
        header={<>{PolygonMeasurementType.Area}:&nbsp;</>}
        value={`${polygonArea} ${areaUnit}`}
        showCopyIcon={!!planarArea}
        isLoading={isLoading}
      />

      {/* Surface Area */}
      <MeasurementInfo
        label={<>{PolygonMeasurementType.SurfaceArea}:&nbsp;</>}
        value={
          surfaceArea ? convertFromSquareMeters(surfaceArea, areaUnit) : null
        }
        isLoading={isLoading}
        unit={areaUnit}
        terrainIterationId={terrainIterationId}
        dsmStatus={dsmStatus}
        dsmCogStatus={dsmCogStatus}
        terrainTileStatus={terrainTileStatus}
      />

      <CopyableText
        header={<>{PolygonMeasurementType.Perimeter}:&nbsp;</>}
        value={`${polygonPerimeter} ${perimeterUnit}`}
        showCopyIcon={!!planarPerimeter}
        isLoading={isLoading}
      />

      <MeasurementInfo
        label={<>{PolygonMeasurementType.SurfacePerimeter}:&nbsp;</>}
        value={
          surfacePerimeter
            ? convertFromMeters(surfacePerimeter, perimeterUnit)
            : null
        }
        isLoading={isLoading}
        unit={perimeterUnit}
        terrainIterationId={terrainIterationId}
        dsmStatus={dsmStatus}
        dsmCogStatus={dsmCogStatus}
        terrainTileStatus={terrainTileStatus}
      />
    </div>
  );
};
